<template>
  <BaseComponent :isReady="isReady" v-if="isShowScanGift">
    <!-- 骨架屏幕 -->
    <ModuleSkeleton slot="loader"></ModuleSkeleton>

    <Container
      v-stat.brow="{
        tag: 'scangift-module.comp-brow|组件曝光',
        ext: biCommonExt,
      }"
      :skin="skinForFixed"
      :title="titleForFixed"
      :titlePrefix="titlePrefix">
      <slot name="header"></slot>

      <!-- 扫码券礼包 -->
      <div class="scangift-list">
        <div class="scangift-list_group" v-for="(group, index) in split3Arr"
          :key="index">
          <span class="scangift-list_group-item" v-for="(item, idx) in group"
            :key="idx">
            <BasicCouponSmall :originalCouponInfo="item"
              :extraParams="handleExtraParams(item)"
              @couponClick="onClickCoupon(item)"
              @btnClick="onClickCouponBtn(item)"></BasicCouponSmall>
            <span class="scangift-list_group-item_tip"
              v-if="handleItemTip(item)">{{ handleItemTip(item) }}</span>
          </span>
        </div>
      </div>

      <slot name="footer"></slot>

      <div class="scangift-rule" v-if="!isHideRule" v-stat.click="{
        tag: 'scangift-module.rule-click|条款及细则点击',
        ext: biCommonExt,
      }" @click="onClickRule">
        <span class="scangift-rule_word">{{ i18n('條款及細則') }}</span>
        <ArrowRightIcon :width="16" color="#576B95"></ArrowRightIcon>
      </div>

      <!-- 条款与细则弹层 -->
      <TncDialog v-if="!isHideRule" ref="refTncDialog" :tncUrl="tncUrlForFixed">
      </TncDialog>
      <!-- 券详情弹层 -->
      <CouponDetailDialog ref="refCouponDetail" :titleText="i18n('優惠券詳情')"
        :goodItemInfo="curDtlItemInfo" :extraInfo="couponDetailExtraInfo"
        :btnInfo="couponDetailBtn" @onBtnClick="onClickDetailBtn">
      </CouponDetailDialog>
      <PromptDialog ref="refPromptDialog" :promptDialogPic="promptDialogPic"
        :onClickPromptDialogBtn="onClickPromptDialogBtn" :biCommonExt="biCommonExt"
      >
        <slot name="promptDialog"></slot>
      </PromptDialog>
    </Container>
  </BaseComponent>
</template>

<script lang="ts">
import { defineComponent, computed, ref, Ref, watch } from 'vue-demi';
import { chunk } from 'lodash-es';
import PromptDialog from './components/prompt-dialog.vue';
import { BaseComponent } from '@tencent/wphk-components-content-loader';
import { BasicCouponSmall } from '@tencent/wphk-act-base-components';
import { TncDialog, CouponDetailDialog } from '@tencent/wphk-act-base-ui';
import { CouponState } from '@tencent/wphk-act-base-types';
import {
  DetailButtonType,
  DetailButtonSize,
} from '@tencent/wphk-act-base-ui/src/components/coupon-detail/types';
import { to } from '@tencent/wphk-base-utils';
import { showBusinessError } from '@tencent/wphk-components-dialog';
import { CustomException, url } from '@tencent/ppd-base-libs';
import { hideLoading, showLoading } from '@tencent/wphk-components-loading';
import { biClick } from '@tencent/fit-bi-sdk';
import { ArrowRightIcon } from '@tencent/wphk-icon-vue';
import { showToast, ToastType } from '@tencent/wphk-components-toast';

import { i18n } from './i18n';

import Container from './components/container.vue';
import ModuleSkeleton from './components/module-skeleton.vue';
import { ItemInfo } from './types';
import {
  checkIsNeedShowBtn,
  go2UsedCoupon,
  isDayLimit,
  getButtonTypeForSmallCoupon,
  getThemeAndTagByFixedCouponType,
  checkIfInAbleStateButDisableTime,
  getLimitTip,
} from './business';
import { useScanGift } from './hooks/use-scan-gift';
import { ExecActState } from '@tencent/wphk-act-base-cgi';

export default defineComponent({
  name: 'ScanGift',
  components: {
    BaseComponent,
    ModuleSkeleton,
    Container,
    BasicCouponSmall,
    ArrowRightIcon,
    PromptDialog,
    TncDialog,
    CouponDetailDialog,
  },
  props: {
    /** 计划Id */
    campaignId: String,
    /** 商户Id */
    spId: String,
    /** 是否隐藏条款与细则 */
    isHideRule: {
      type: Boolean,
      default: false,
    },
    /** 派发渠道 */
    disSource: {
      type: String,
      default: '',
    },
    /** 执行领券前的预校验 */
    preCheckReceiveCoupon: {
      type: Function,
      required: false,
    },
    /** 自定义券配置 */
    couponConfig: {
      type: Object,
      required: false,
    },
    /** 顶部标题前缀 */
    titlePrefix: {
      type: String,
      required: false,
    },
  },
  emits: [
    // 通知外部，组件展示
    'showState',
    // 通知外部：组件加载完毕
    'isReady',
  ],
  setup(props, { emit }) {
    // ---- ref句柄 ----
    const refTncDialog: Ref<InstanceType<typeof TncDialog> | null> = ref(null);
    const refCouponDetail: Ref<InstanceType<typeof CouponDetailDialog> | null> = ref(null);
    const refPromptDialog: Ref<InstanceType<typeof PromptDialog> | null> = ref(null);

    // ---- hooks ----
    const {
      isShowScanGift,
      fixedCouponDetailList,
      tncUrlForFixed,
      skinForFixed,
      titleForFixed,
      promptDialogPic,
      finalCouponInfo,

      initScanGift,
      receiveFixedCoupon,
      updateFixedCoupon,
    } = useScanGift();

    // ---- 监听props ---
    watch(
      [() => props.campaignId, () => props.spId],
      async ([campaignId, spId]) => {
        console.log('useScanGift props campaignId/spId', campaignId, spId);
        if (!campaignId || !spId) return;
        // 初始化
        await initScanGift(campaignId, spId);
        // 组件初始化完毕，emit事件到外部
        emit('isReady', isReady.value, {
          titleForFixed: titleForFixed.value,
          fixedCouponDetailList: fixedCouponDetailList.value,
        }); // isReady为true则组件展示，为false则不展示
      },
      {
        immediate: true, // 首次需要立即执行
      },
    );
    watch(isShowScanGift, (nVal) => {
      emit('showState', nVal); // 因为迎新模块要感知扫码券礼包模块的展示情况，所以将展示结果emit出去
    });

    // ---- data ----
    // 券详情弹层-当前点击的coupon信息（用GoodItem）
    const curDtlItemInfo: Ref<ItemInfo> = ref(fixedCouponDetailList.value[0]);

    // ---- computed ----
    const isReady = computed(() => fixedCouponDetailList.value.length > 0);
    // 划分为3个一组
    const split3Arr = computed(() => chunk(fixedCouponDetailList.value, 3));
    // 券详情弹层-btn信息
    const couponDetailBtn = computed(() => {
      const findConf = finalCouponInfo.value.find(it => it.actIdEn === curDtlItemInfo.value?.getCouponParams.enActid);

      // 只有‘立即领取’和‘去使用’有btn
      if (
        !checkIsNeedShowBtn(curDtlItemInfo.value?.couponState)
        || checkIfInAbleStateButDisableTime(
          findConf?.ableTimeList ?? [],
          curDtlItemInfo.value?.couponState,
        )
      ) return {
        isShowBtn: false,
      };

      return {
        isShowBtn: true,
        isDisabled: false,
        btnText:
          curDtlItemInfo.value?.couponState === CouponState.notReceived
            ? i18n('立即領取')
            : i18n('去使用'),
        type:
          curDtlItemInfo.value?.couponState === CouponState.notReceived
            ? DetailButtonType.primary
            : DetailButtonType.ghost,
        size: DetailButtonSize.normal,
      };
    });
    // 券详情弹层-额外的信息(coupon-v2.vue使用)
    const couponDetailExtraInfo = computed(() => {
      const state2DescMap = {
        [CouponState.received]: i18n('已領取'),
        [CouponState.used]: i18n('已使用'),
        [CouponState.expired]: i18n('已過期'),
      };
      return {
        stampText: state2DescMap[curDtlItemInfo.value?.couponState] ?? '',
        isExpire: CouponState.expired === curDtlItemInfo.value?.couponState,
      };
    });
    // 上报的扩展字段
    const biCommonExt = computed(() => ({
      fmtn: url.getUrlParam('mtn') || url.getUrlParam('MTN'), // 物料MTN
      fbatchid: url.getUrlParam('batchid'), // 物料批次Id
      fcampaignid: url.getUrlParam('campaignid'), // 物料计划Id
      fspid: props.spId, // 商户Id
    }));

    // ---- 事件 ----
    // 点击条款与细则
    function onClickRule() {
      refTncDialog.value?.showTncDialog();
    }
    function showPromptDialog() {
      refPromptDialog.value.showDialog();
    }
    // 点击券面-拉起详情弹层
    function onClickCoupon(item) {
      curDtlItemInfo.value = item;
      // @ts-ignore
      refCouponDetail.value?.showDialog();

      biClick('scangift-module.couponsmall-comp-click|券面点击', {
        ...biCommonExt.value,
        fcouponName: item.couponName,
        fcouponId: item.goodsId, // 券Id
        fcouponState: item.couponState,
      });
    }
    // 点击券内的btn
    function onClickCouponBtn(item) {
      // 先上报，应为可能会有跳转出去
      biClick('scangift-module.couponsmall-btn-click|券btn点击', {
        ...biCommonExt.value,
        fcouponName: item.couponName,
        fcouponId: item.goodsId, // 券Id
        fcouponState: item.couponState,
      });

      receiveOrUseCoupon(item);
    }
    // 点击券详情内的btn
    function onClickDetailBtn() {
      receiveOrUseCoupon(curDtlItemInfo.value);
    }

    // ---- 辅助方法 ----
    // 领券或使用券
    async function receiveOrUseCoupon(item: ItemInfo) {
      if (item.couponState === CouponState.notReceived) {
        // 预处理校验函数
        if (
          typeof props.preCheckReceiveCoupon === 'function'
          && props.preCheckReceiveCoupon
        ) {
          const [err] = await to(props.preCheckReceiveCoupon(item));
          if (err) {
            return;
          }
        }
        receiveCoupon(item);
      }
      if (item.couponState === CouponState.received) {
        go2UsedCoupon(item.couponType, item.userCouponid);
      }
    }
    /** 点击弹窗「立即领取」按钮 */
    async function onClickPromptDialogBtn() {
      // 预处理校验函数
      if (
        typeof props.preCheckReceiveCoupon === 'function'
        && props.preCheckReceiveCoupon
      ) {
        const [err] = await to(props.preCheckReceiveCoupon(fixedCouponDetailList.value));
        if (err) {
          return;
        }
      }
      receiveAllCoupon();
    }
    /** 领取所有券，并更新 */
    async function receiveAllCoupon() {
      // 过滤未领过的券
      const unReceivedCoupon = fixedCouponDetailList.value.filter(item => item.couponState === CouponState.notReceived);
      if (unReceivedCoupon.length === 0) {
        return;
      }
      showLoading();
      const [err] = await to(Promise.all(unReceivedCoupon.map((item) => {
        const { enActid, prizeId } = item.getCouponParams;
        return receiveFixedCoupon({ enActid, prizeId, disSource: props.disSource });
      })));
      hideLoading();
      if (err) {
        const { options } = err as any;
        const execState = options?.execState;
        new CustomException(
          err,
          'ScanGiftGetAllCouponException',
          `get coupon fail. execState: ${execState}`,
        );
        showToast({
          title: '好像出錯了<br/>請稍後再試',
          type: ToastType.Exclamation,
        });
      } else {
        // 领取成功提示
        showToast({
          title: i18n('領取成功'),
        });
      }
      /** 更新券 */
      await to(updateFixedCoupon());
    }

    // 领券并更新
    async function receiveCoupon(item: ItemInfo) {
      const { enActid, prizeId } = item.getCouponParams;
      showLoading();
      const [err] = await to(receiveFixedCoupon({ enActid, prizeId, disSource: props.disSource }));
      hideLoading();

      if (err) {
        showBusinessError(err);
        const { options } = err as any;
        const execState = options?.execState;
        new CustomException(
          err,
          'ScanGiftGetCouponException',
          `get coupon fail. execState: ${execState}`,
        );
        if (execState !== ExecActState.stockNotEnough) {
          return;
        }
      } else {
        // 领取成功提示
        showToast({
          title: i18n('領取成功'),
        });
      }

      // 成功，更新状态
      const [err2, updateItem] = await to(updateFixedCoupon(item));
      if (err2 || !updateItem) {
        // 不提示
        throw new CustomException(
          err2,
          'ScanGiftUpdateCouponException',
          'update coupon fail',
        );
      }
      // 更新当前券弹层的信息
      curDtlItemInfo.value = updateItem;
    }

    /** 外部传入的券配置 */
    function getCouponConfig() {
      return props.couponConfig || {};
    }

    // 处理传给basicCouponSmall的extraParams，extraParams是用来覆盖couponInfo的
    function handleExtraParams(item: ItemInfo) {
      const findConf = finalCouponInfo.value.find(it => it.actIdEn === item.getCouponParams.enActid);
      const isInAbleStateButDisableTime = checkIfInAbleStateButDisableTime(
        findConf?.ableTimeList ?? [],
        item.couponState,
      );
      return {
        prizeDesc: item.couponRange || i18n('精選門店可用'),
        ...getButtonTypeForSmallCoupon(item, findConf?.ableTimeList ?? []),
        ...(findConf ? getThemeAndTagByFixedCouponType(findConf?.type) : {}),
        ...getCouponConfig(),
        tipsText: isInAbleStateButDisableTime
          ? i18n('未到领券时间')
          : getLimitTip(item.exchangeState),
        isShowButton: !isInAbleStateButDisableTime && item.isShowButton,
      };
    }

    // 券底部提示文案
    function handleItemTip(item: ItemInfo): string {
      if (isDayLimit(item.exchangeState)) return i18n('明日00:00可領'); // 达到日限额提醒

      return '';
    }

    return {
      refTncDialog,
      refCouponDetail,
      refPromptDialog,

      isShowScanGift,
      isReady,
      fixedCouponDetailList,
      tncUrlForFixed,
      skinForFixed,
      titleForFixed,
      promptDialogPic,

      split3Arr,
      couponDetailBtn,
      couponDetailExtraInfo,
      curDtlItemInfo,

      biCommonExt,

      onClickRule,
      onClickCoupon,
      onClickCouponBtn,
      onClickDetailBtn,
      onClickPromptDialogBtn,
      handleExtraParams,
      handleItemTip,
      receiveCoupon,
      receiveAllCoupon,
      showPromptDialog,

      i18n,
    };
  },
});
</script>

<style scoped lang="less">
@import (reference) './less/main.less';

.scangift {
  &-list {
    margin-top: 16 / @rem;

    &_group {
      &:not(:first-child) {
        margin-top: 23 / @rem;
      }

      display: flex;
      flex-direction: row;
      justify-content: flex-start;

      &-item {
        /deep/ .amount-box {
          .currency {
            font-size: 28 / @rem !important;
            line-height: 31 / @rem !important;
          }

          .amount {
            font-size: 72 / @rem !important;
            line-height: 80 / @rem !important;
          }
        }

        /deep/ .coupon-desc {
          white-space: pre-wrap;
        }

        /deep/ .coupon__disabled .tips-text {
          opacity: 1;
          color: rgba(0, 0, 0, 0.5);
        }

        margin-right: 23 / @rem;

        &:last-child {
          margin-right: 0;
        }

        text-align: center;

        &_tip {
          display: block;
          margin-top: 4 / @rem;
          color: rgba(0, 0, 0, 0.2);
          font-size: 20 / @rem;
          font-weight: 400;
          line-height: 28 / @rem;
          max-width: 192 / @rem; // 文案超长时不要超出券UI宽度
        }
      }
    }
  }

  &-rule {
    margin-top: 24 / @rem;
    text-align: center;

    &:active {
      opacity: 0.5;
    }

    display: flex;
    flex-direction: row;
    justify-content: center;
    align-items: center;

    &_word {
      margin-right: 8 / @rem;

      color: #576b95;
      font-size: 24 / @rem;
      font-style: normal;
      font-weight: 400;
      line-height: 32 / @rem;
    }
  }
}
</style>

<style lang="less">
@import (reference) './less/main.less';

.en {
  .scangift-list_group-item_tip {
    font-size: 18 / @rem;
    line-height: 22 / @rem;
  }

  .coupon-desc {
    font-size: 20 / @rem !important;
    line-height: 24 / @rem;
  }
}
</style>

<style lang="less" scoped>
@import './less/skin.less';
@import './less/spring-skin.less';
</style>
